import React, { useState } from 'react';
import { motion } from 'framer-motion';
import LiquidGlassBox from './Box';
import { Button } from '../index';

const BoxDemo = () => {
  const [activeCard, setActiveCard] = useState(null);

  const handleCardClick = (id) => {
    setActiveCard(activeCard === id ? null : id);
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: `
        linear-gradient(135deg, #667eea 0%, #764ba2 100%),
        radial-gradient(circle at 20% 80%, #f093fb 0%, transparent 50%),
        radial-gradient(circle at 80% 20%, #f5576c 0%, transparent 50%),
        radial-gradient(circle at 40% 40%, #4facfe 0%, transparent 50%)
      `,
      backgroundBlendMode: 'overlay, screen, screen, screen',
      padding: '40px 20px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '40px',
      fontFamily: 'system-ui, -apple-system, sans-serif'
    }}>
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        style={{
          textAlign: 'center',
          color: 'white',
          marginBottom: '20px'
        }}
      >
        <h1 style={{
          fontSize: '3rem',
          fontWeight: 'bold',
          margin: '0 0 10px 0',
          textShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
          background: 'linear-gradient(45deg, #fff, #f0f0f0)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text'
        }}>
          Liquid Glass Box
        </h1>
        <p style={{
          fontSize: '1.2rem',
          opacity: 0.9,
          margin: '0',
          textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
        }}>
          Versatile glass morphism containers for cards, panels, and layouts
        </p>
      </motion.div>

      {/* Variants Section */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.2 }}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '20px',
          width: '100%',
          maxWidth: '1200px'
        }}
      >
        <h2 style={{
          color: 'white',
          fontSize: '2rem',
          margin: '0',
          textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
        }}>
          Box Variants
        </h2>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '20px',
          width: '100%'
        }}>
          <LiquidGlassBox variant="primary" size="medium">
            <h3 style={{ margin: '0 0 10px 0', fontSize: '1.2rem' }}>Primary Box</h3>
            <p style={{ margin: 0, opacity: 0.8, lineHeight: 1.5 }}>
              This is a primary glass box with beautiful morphism effects and smooth animations.
            </p>
          </LiquidGlassBox>
          
          <LiquidGlassBox variant="secondary" size="medium">
            <h3 style={{ margin: '0 0 10px 0', fontSize: '1.2rem' }}>Secondary Box</h3>
            <p style={{ margin: 0, opacity: 0.8, lineHeight: 1.5 }}>
              A secondary variant with subtle transparency and elegant styling.
            </p>
          </LiquidGlassBox>
          
          <LiquidGlassBox variant="success" size="medium">
            <h3 style={{ margin: '0 0 10px 0', fontSize: '1.2rem' }}>Success Box</h3>
            <p style={{ margin: 0, opacity: 0.8, lineHeight: 1.5 }}>
              Perfect for success messages and positive feedback displays.
            </p>
          </LiquidGlassBox>
          
          <LiquidGlassBox variant="danger" size="medium">
            <h3 style={{ margin: '0 0 10px 0', fontSize: '1.2rem' }}>Danger Box</h3>
            <p style={{ margin: 0, opacity: 0.8, lineHeight: 1.5 }}>
              Ideal for error messages and critical information alerts.
            </p>
          </LiquidGlassBox>
          
          <LiquidGlassBox variant="warning" size="medium">
            <h3 style={{ margin: '0 0 10px 0', fontSize: '1.2rem' }}>Warning Box</h3>
            <p style={{ margin: 0, opacity: 0.8, lineHeight: 1.5 }}>
              Great for warnings and important notices that need attention.
            </p>
          </LiquidGlassBox>
          
          <LiquidGlassBox variant="info" size="medium">
            <h3 style={{ margin: '0 0 10px 0', fontSize: '1.2rem' }}>Info Box</h3>
            <p style={{ margin: 0, opacity: 0.8, lineHeight: 1.5 }}>
              Perfect for informational content and helpful tips.
            </p>
          </LiquidGlassBox>
        </div>
      </motion.div>

      {/* Sizes Section */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.4 }}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '20px',
          width: '100%',
          maxWidth: '1200px'
        }}
      >
        <h2 style={{
          color: 'white',
          fontSize: '2rem',
          margin: '0',
          textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
        }}>
          Box Sizes
        </h2>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
          width: '100%'
        }}>
          <LiquidGlassBox size="small" variant="primary">
            <h3 style={{ margin: '0 0 8px 0', fontSize: '1rem' }}>Small Box</h3>
            <p style={{ margin: 0, opacity: 0.8, fontSize: '0.9rem' }}>
              Compact size perfect for small content blocks and quick info.
            </p>
          </LiquidGlassBox>
          
          <LiquidGlassBox size="medium" variant="secondary">
            <h3 style={{ margin: '0 0 10px 0', fontSize: '1.2rem' }}>Medium Box (Default)</h3>
            <p style={{ margin: 0, opacity: 0.8, lineHeight: 1.5 }}>
              Standard size for most content, balanced padding and spacing.
            </p>
          </LiquidGlassBox>
          
          <LiquidGlassBox size="large" variant="success">
            <h3 style={{ margin: '0 0 12px 0', fontSize: '1.4rem' }}>Large Box</h3>
            <p style={{ margin: 0, opacity: 0.8, lineHeight: 1.6, fontSize: '1.1rem' }}>
              Spacious container for detailed content, forms, and complex layouts.
            </p>
          </LiquidGlassBox>
          
          <LiquidGlassBox size="xlarge" variant="info">
            <h3 style={{ margin: '0 0 15px 0', fontSize: '1.6rem' }}>Extra Large Box</h3>
            <p style={{ margin: 0, opacity: 0.8, lineHeight: 1.7, fontSize: '1.2rem' }}>
              Maximum size for hero sections, full-page content, and prominent displays.
            </p>
          </LiquidGlassBox>
        </div>
      </motion.div>

      {/* Interactive Cards */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.6 }}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '20px',
          width: '100%',
          maxWidth: '1200px'
        }}
      >
        <h2 style={{
          color: 'white',
          fontSize: '2rem',
          margin: '0',
          textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
        }}>
          Interactive Cards
        </h2>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '20px',
          width: '100%'
        }}>
          <LiquidGlassBox
            variant="primary"
            size="medium"
            onClick={() => handleCardClick('card1')}
            style={{ cursor: 'pointer' }}
          >
            <div style={{ textAlign: 'center' }}>
              <h3 style={{ margin: '0 0 10px 0', fontSize: '1.3rem' }}>🎨 Design Card</h3>
              <p style={{ margin: '0 0 15px 0', opacity: 0.8, lineHeight: 1.5 }}>
                Beautiful glass morphism design with interactive hover effects.
              </p>
              <Button variant="secondary" size="small">
                Learn More
              </Button>
            </div>
          </LiquidGlassBox>
          
          <LiquidGlassBox
            variant="success"
            size="medium"
            onClick={() => handleCardClick('card2')}
            style={{ cursor: 'pointer' }}
          >
            <div style={{ textAlign: 'center' }}>
              <h3 style={{ margin: '0 0 10px 0', fontSize: '1.3rem' }}>⚡ Performance</h3>
              <p style={{ margin: '0 0 15px 0', opacity: 0.8, lineHeight: 1.5 }}>
                Optimized animations and smooth transitions for the best user experience.
              </p>
              <Button variant="primary" size="small">
                Explore
              </Button>
            </div>
          </LiquidGlassBox>
          
          <LiquidGlassBox
            variant="warning"
            size="medium"
            onClick={() => handleCardClick('card3')}
            style={{ cursor: 'pointer' }}
          >
            <div style={{ textAlign: 'center' }}>
              <h3 style={{ margin: '0 0 10px 0', fontSize: '1.3rem' }}>🔧 Customizable</h3>
              <p style={{ margin: '0 0 15px 0', opacity: 0.8, lineHeight: 1.5 }}>
                Highly customizable with multiple variants, sizes, and styling options.
              </p>
              <Button variant="danger" size="small">
                Customize
              </Button>
            </div>
          </LiquidGlassBox>
        </div>
      </motion.div>

      {/* Features List */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1.2 }}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '20px',
          width: '100%',
          maxWidth: '1200px'
        }}
      >
        <h2 style={{
          color: 'white',
          fontSize: '2rem',
          margin: '0',
          textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
        }}>
          Box Features
        </h2>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '20px',
          width: '100%'
        }}>
          <div style={{
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            borderRadius: '12px',
            padding: '20px',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}>
            <h3 style={{ color: 'white', margin: '0 0 10px 0' }}>Glass Effects</h3>
            <ul style={{ color: 'white', margin: 0, paddingLeft: '20px' }}>
              <li>backdrop-filter: blur(15-30px)</li>
              <li>Multi-layered gradients</li>
              <li>Glass borders & shadows</li>
              <li>Corner highlights</li>
              <li>Inner glow effects</li>
            </ul>
          </div>
          <div style={{
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            borderRadius: '12px',
            padding: '20px',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}>
            <h3 style={{ color: 'white', margin: '0 0 10px 0' }}>Animations</h3>
            <ul style={{ color: 'white', margin: 0, paddingLeft: '20px' }}>
              <li>Parallax mouse tracking</li>
              <li>Hover scale effects</li>
              <li>Spring animations</li>
              <li>Content lift effects</li>
              <li>Glow transitions</li>
            </ul>
          </div>
          <div style={{
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            borderRadius: '12px',
            padding: '20px',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}>
            <h3 style={{ color: 'white', margin: '0 0 10px 0' }}>Customization</h3>
            <ul style={{ color: 'white', margin: 0, paddingLeft: '20px' }}>
              <li>7 color variants</li>
              <li>4 size options</li>
              <li>Custom dimensions</li>
              <li>Flexible styling</li>
              <li>Interactive options</li>
            </ul>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default BoxDemo; 